(function() {
  'use strict';

  /* ============================================
     LBDB Bracelet Configurator
     v2.0 - Corrections: accessibilité, XSS, validation, performance
     ============================================ */

  const configurator = document.getElementById('bracelet-configurator');
  if (!configurator) return;

  const BRACELET_COLLECTION = configurator.dataset.braceletCollection;
  const CHARM_COLLECTION = configurator.dataset.charmCollection;
  const MAX_CHARMS = parseInt(configurator.dataset.maxCharms, 10) || 5;
  const LOCALE = configurator.dataset.locale || 'fr';
  const CATEGORY_TRANSLATIONS = (() => {
    try { return JSON.parse(configurator.dataset.categoryTranslations || '{}'); }
    catch(e) { return {}; }
  })();

  // State
  let currentStep = 1;
  let selectedBracelet = null;
  let selectedBraceletVariantId = null; // Selected size variant
  let selectedCharms = [];
  let bracelets = [];
  let charms = [];
  let charmCategories = [];
  let activeFilter = 'all';
  let searchQuery = '';
  let isAddingToCart = false; // Guard against race conditions

  // DOM refs
  const panels = configurator.querySelectorAll('.configurator__panel');
  const steps = configurator.querySelectorAll('.configurator__step');
  const stepLines = configurator.querySelectorAll('.configurator__step-line');
  const braceletGrid = document.getElementById('bracelet-grid');
  const charmGrid = document.getElementById('charm-grid');
  const charmFilters = document.getElementById('charm-filters');
  const selectedBar = document.getElementById('selected-charms-bar');
  const selectedList = document.getElementById('selected-charms-list');
  const btnPrev = document.getElementById('btn-prev');
  const btnNext = document.getElementById('btn-next');
  const btnToStep3 = document.getElementById('btn-to-step3');
  const btnToStep2 = document.getElementById('btn-to-step2');
  const braceletSelectedBar = document.getElementById('bracelet-selected-bar');
  const braceletSelectedName = document.getElementById('bracelet-selected-name');
  const btnAddToCart = document.getElementById('btn-add-to-cart');
  const charmSearch = document.getElementById('charm-search');
  const charmSearchClear = document.getElementById('charm-search-clear');
  const stickyBar = document.getElementById('charm-sticky-bar');

  /* --- i18n: Read translations from data-attributes (set by Liquid) --- */
  const i18n = {
    loadingBracelets: configurator.dataset.i18nLoadingBracelets || 'Chargement des bracelets...',
    loadingCharms: configurator.dataset.i18nLoadingCharms || 'Chargement des charms...',
    noBracelets: configurator.dataset.i18nNoBracelets || 'Aucun bracelet disponible',
    noCharms: configurator.dataset.i18nNoCharms || 'Aucun charm disponible',
    noResults: configurator.dataset.i18nNoResults || 'Aucun charm trouvé',
    filterAll: configurator.dataset.i18nFilterAll || 'Tous',
    selected: configurator.dataset.i18nSelected || 'sélectionné',
    addToCart: configurator.dataset.i18nAddToCart || 'Ajouter au panier',
    adding: configurator.dataset.i18nAdding || 'Ajout en cours...',
    savings: configurator.dataset.i18nSavings || 'Vous économisez <strong>{{ amount }}</strong> !',
    free: configurator.dataset.i18nFree || 'OFFERT',
    zoom: configurator.dataset.i18nZoom || "Zoomer sur l'image",
    successTitle: configurator.dataset.i18nSuccessTitle || 'Félicitations !',
    successMsg: configurator.dataset.i18nSuccessMsg || 'Ton bracelet personnalisé a bien été ajouté au panier.',
    successNew: configurator.dataset.i18nSuccessNew || 'Composer un nouveau bracelet',
    successHome: configurator.dataset.i18nSuccessHome || "Retour à l'accueil",
    chooseSize: configurator.dataset.i18nChooseSize || 'Choisis ta taille pour continuer'
  };

  /* --- Screen Reader Announcer (Accessibility) --- */
  const srAnnouncer = document.createElement('div');
  srAnnouncer.id = 'sr-announcer';
  srAnnouncer.setAttribute('aria-live', 'polite');
  srAnnouncer.setAttribute('aria-atomic', 'true');
  srAnnouncer.className = 'configurator__sr-only';
  document.body.appendChild(srAnnouncer);

  function announce(message) {
    srAnnouncer.textContent = '';
    setTimeout(() => { srAnnouncer.textContent = message; }, 50);
  }

  /* --- Utility: Escape HTML (XSS Protection) --- */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /* --- Utility: Validate Product Data --- */
  function validateProduct(product) {
    return product &&
           product.id &&
           product.title &&
           product.variants &&
           product.variants.length > 0 &&
           product.variants[0].id &&
           typeof product.variants[0].price !== 'undefined';
  }

  /* --- Sticky Observer --- */
  let stickyObserver = null;
  if (stickyBar) {
    const sentinel = document.createElement('div');
    sentinel.style.height = '1px';
    sentinel.style.marginBottom = '-1px';
    stickyBar.parentNode.insertBefore(sentinel, stickyBar);

    stickyObserver = new IntersectionObserver(
      ([entry]) => {
        stickyBar.classList.toggle('configurator__sticky-bar--stuck', !entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px' }
    );
    stickyObserver.observe(sentinel);
  }

  /* --- Promo Evergreen Logic ---
     Shopify has 2 active BXGY automatic discounts:
     - "2ème à -50%": Buy 1, Get 1 at 50% off (applies per pair, cheapest item discounted)
     - "3ème OFFERT": Buy 2, Get 1 Free (applies per lot of 3, cheapest item free)
     Both have usesPerOrderLimit = null (unlimited applications).
     Shopify applies the BEST offer for the customer (highest total savings).
     Items are sorted by price DESC; the cheapest in each group gets the discount.
  */
  function calculatePromo() {
    if (!selectedBracelet) return { items: [], totalBefore: 0, totalAfter: 0, savings: 0 };

    // Build items list preserving insertion order for display
    const allItems = [];
    allItems.push({
      product: selectedBracelet,
      role: 'base',
      originalPrice: parseFloat(selectedBracelet.variants[0].price),
      addOrder: 0
    });
    selectedCharms.forEach((charm, idx) => {
      allItems.push({
        product: charm,
        role: 'charm',
        originalPrice: parseFloat(charm.variants[0].price),
        addOrder: idx + 1
      });
    });

    // Sort items by price descending for discount calculation
    const sorted = [...allItems].sort((a, b) => b.originalPrice - a.originalPrice);

    // --- Offer A: "2ème à -50%" (Buy 1 Get 1 at 50% off) ---
    // Per pair of items, the cheaper one (2nd in pair) gets -50%
    const offerA = { savings: 0, discounts: new Map() };
    for (let i = 0; i < Math.floor(sorted.length / 2); i++) {
      const discountedItem = sorted[i * 2 + 1]; // cheaper item in pair
      const saving = discountedItem.originalPrice * 0.5;
      offerA.savings += saving;
      offerA.discounts.set(discountedItem, { price: discountedItem.originalPrice * 0.5, label: '-50%' });
    }

    // --- Offer B: "3ème OFFERT" (Buy 2 Get 1 Free) ---
    // Per lot of 3 items, the cheapest one (3rd in lot) is free
    const offerB = { savings: 0, discounts: new Map() };
    for (let i = 0; i < Math.floor(sorted.length / 3); i++) {
      const discountedItem = sorted[i * 3 + 2]; // cheapest item in lot of 3
      const saving = discountedItem.originalPrice;
      offerB.savings += saving;
      offerB.discounts.set(discountedItem, { price: 0, label: i18n.free });
    }

    // Choose the best offer for the customer (highest savings)
    const bestOffer = (offerB.savings >= offerA.savings) ? offerB : offerA;

    // Apply discounts to items
    allItems.forEach(item => {
      item.discountedPrice = item.originalPrice;
      item.discountLabel = null;
      const disc = bestOffer.discounts.get(item);
      if (disc) {
        item.discountedPrice = disc.price;
        item.discountLabel = disc.label;
      }
    });

    const totalBefore = allItems.reduce((sum, i) => sum + i.originalPrice, 0);
    const totalAfter = allItems.reduce((sum, i) => sum + i.discountedPrice, 0);

    return {
      items: allItems,
      totalBefore: totalBefore,
      totalAfter: totalAfter,
      savings: totalBefore - totalAfter
    };
  }

  /* --- Navigation --- */
  function goToStep(step) {
    currentStep = step;

    panels.forEach(p => {
      p.classList.toggle('configurator__panel--active', parseInt(p.dataset.panel) === step);
    });

    steps.forEach((s, i) => {
      const stepNum = i + 1;
      s.classList.toggle('configurator__step--active', stepNum === step);
      s.classList.toggle('configurator__step--completed', stepNum < step);
    });

    stepLines.forEach((line, i) => {
      line.classList.toggle('configurator__step-line--completed', i + 1 < step);
    });

    btnPrev.style.display = step > 1 ? 'block' : 'none';
    if (step === 1) {
      btnNext.style.display = 'none';
      braceletSelectedBar.style.display = selectedBracelet ? 'flex' : 'none';
    } else if (step === 2) {
      btnNext.style.display = 'none';
      braceletSelectedBar.style.display = 'none';
    } else {
      btnNext.style.display = 'none';
      braceletSelectedBar.style.display = 'none';
    }

    if (step === 3) {
      selectedBar.style.display = 'none';
      renderSummary();
    }

    configurator.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Focus management (Accessibility)
    setTimeout(() => {
      const activePanel = configurator.querySelector(`[data-panel="${step}"]`);
      if (activePanel) {
        const firstFocusable = activePanel.querySelector('button, input, [tabindex="0"]');
        if (firstFocusable) firstFocusable.focus({ preventScroll: true });
      }
    }, 350);

    announce(`Étape ${step} sur 3`);
  }

  btnPrev.addEventListener('click', () => {
    if (currentStep > 1) goToStep(currentStep - 1);
  });

  btnNext.addEventListener('click', () => {
    if (currentStep === 2 && selectedCharms.length > 0) goToStep(3);
  });

  btnToStep2.addEventListener('click', () => {
    if (!selectedBraceletVariantId) {
      // Highlight the size selector if no size chosen
      const sizeSelector = braceletGrid.querySelector('.configurator__size-selector');
      if (sizeSelector) {
        sizeSelector.classList.add('configurator__size-selector--shake');
        setTimeout(() => sizeSelector.classList.remove('configurator__size-selector--shake'), 600);
      }
      return;
    }
    goToStep(2);
  });

  btnToStep3.addEventListener('click', () => {
    goToStep(3);
  });

  // Allow clicking on stepper steps to navigate back to previous steps
  steps.forEach((stepEl, i) => {
    const stepNum = i + 1;
    stepEl.style.cursor = 'pointer';
    stepEl.setAttribute('role', 'button');
    stepEl.setAttribute('aria-label', `Aller à l'étape ${stepNum}`);
    stepEl.addEventListener('click', () => {
      // Can only navigate to completed steps or current step (i.e., go back)
      if (stepNum < currentStep) {
        goToStep(stepNum);
      }
    });
    stepEl.addEventListener('keydown', (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && stepNum < currentStep) {
        e.preventDefault();
        goToStep(stepNum);
      }
    });
  });

  /* --- Fetch Products --- */
  const rootUrl = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';

  async function fetchCollectionProducts(handle) {
    let allProducts = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const url = `${rootUrl}collections/${handle}/products.json?limit=250&page=${page}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status} pour la collection "${handle}"`);
      }

      const data = await response.json();

      if (data.products && data.products.length > 0) {
        allProducts = allProducts.concat(data.products);
        page++;
        if (data.products.length < 250) hasMore = false;
      } else {
        hasMore = false;
      }
    }

    return allProducts;
  }

  /* --- Create Product Card (Safe DOM creation, no innerHTML XSS) --- */
  function createProductCard(product, type) {
    const card = document.createElement('div');
    card.className = 'configurator__card';
    card.dataset.productId = product.id;
    card.dataset.variantId = product.variants[0].id;

    // Accessibility: role, tabindex, aria
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
    const customName = getCustomName(product);
    const priceText = formatPrice(product.variants[0].price);
    card.setAttribute('aria-label', `${customName} - ${priceText}`);
    card.setAttribute('aria-pressed', 'false');

    // Image wrapper (for zoom button positioning)
    const imgWrapper = document.createElement('div');
    imgWrapper.className = 'configurator__card-image-wrapper';

    const img = document.createElement('img');
    img.className = 'configurator__card-image configurator__card-image--loading';
    const imageSrc = (product.images && product.images[0]) ? product.images[0].src : '';
    img.src = resizeImage(imageSrc, type === 'bracelet' ? '300x300' : '200x200');
    img.alt = product.title;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.onload = function() { this.classList.remove('configurator__card-image--loading'); };
    img.onerror = function() { this.classList.remove('configurator__card-image--loading'); };

    // Zoom button (magnifying glass)
    const zoomBtn = document.createElement('button');
    zoomBtn.className = 'configurator__card-zoom';
    zoomBtn.setAttribute('aria-label', `${i18n.zoom} - ${customName}`);
    zoomBtn.setAttribute('type', 'button');
    zoomBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>';
    zoomBtn.addEventListener('click', (e) => {
      e.stopPropagation(); // Don't trigger card selection
      openZoomModal(imageSrc, customName);
    });

    imgWrapper.appendChild(img);
    imgWrapper.appendChild(zoomBtn);

    // Info row
    const info = document.createElement('div');
    info.className = 'configurator__card-info';

    // Type charm (e.g. "Charm pendant" or "Charm classique") - displayed above the name
    const typeCharm = getTypeCharm(product);
    if (typeCharm) {
      const typeSpan = document.createElement('span');
      typeSpan.className = 'configurator__card-type';
      typeSpan.textContent = typeCharm;
      info.appendChild(typeSpan);
    }

    const nameSpan = document.createElement('span');
    nameSpan.className = 'configurator__card-name';
    nameSpan.textContent = customName;

    const priceSpan = document.createElement('span');
    priceSpan.className = 'configurator__card-price';
    priceSpan.textContent = priceText;

    info.appendChild(nameSpan);
    info.appendChild(priceSpan);
    card.appendChild(imgWrapper);
    card.appendChild(info);

    return card;
  }

  /* --- Zoom Modal --- */
  let zoomModal = null;

  function createZoomModal() {
    zoomModal = document.createElement('div');
    zoomModal.className = 'configurator__zoom-modal';
    zoomModal.setAttribute('role', 'dialog');
    zoomModal.setAttribute('aria-modal', 'true');
    zoomModal.setAttribute('aria-label', 'Zoom produit');
    zoomModal.innerHTML = `
      <div class="configurator__zoom-overlay"></div>
      <div class="configurator__zoom-content">
        <button class="configurator__zoom-close" aria-label="Fermer">&times;</button>
        <img class="configurator__zoom-image" src="" alt="" />
      </div>
    `;
    document.body.appendChild(zoomModal);

    // Close on overlay click
    zoomModal.querySelector('.configurator__zoom-overlay').addEventListener('click', closeZoomModal);
    // Close on X button
    zoomModal.querySelector('.configurator__zoom-close').addEventListener('click', closeZoomModal);
    // Close on Escape
    zoomModal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeZoomModal();
    });
  }

  function openZoomModal(imageSrc, altText) {
    if (!zoomModal) createZoomModal();
    const img = zoomModal.querySelector('.configurator__zoom-image');
    // Use moderately sized image for fast loading
    img.src = resizeImage(imageSrc, '400x400');
    img.alt = altText || '';
    zoomModal.classList.add('configurator__zoom-modal--open');
    document.body.style.overflow = 'hidden';
    zoomModal.querySelector('.configurator__zoom-close').focus();
  }

  function closeZoomModal() {
    if (!zoomModal) return;
    zoomModal.classList.remove('configurator__zoom-modal--open');
    document.body.style.overflow = '';
  }

  /* --- Render Bracelets --- */
  function renderBracelets() {
    braceletGrid.innerHTML = '';

    const validBracelets = bracelets.filter(validateProduct);

    if (validBracelets.length === 0) {
      const msg = document.createElement('p');
      msg.style.textAlign = 'center';
      msg.style.color = '#999';
      msg.textContent = i18n.noBracelets;
      braceletGrid.appendChild(msg);
      return;
    }

    validBracelets.forEach(product => {
      const card = createProductCard(product, 'bracelet');

      const handleSelect = () => selectBracelet(product, card);
      card.addEventListener('click', handleSelect);
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      });

      braceletGrid.appendChild(card);
    });
  }

  function selectBracelet(product, card) {
    const prev = braceletGrid.querySelector('.configurator__card--selected');
    if (prev) {
      prev.classList.remove('configurator__card--selected');
      prev.setAttribute('aria-pressed', 'false');
      // Remove size selector from previous card
      const prevSize = prev.querySelector('.configurator__size-selector');
      if (prevSize) prevSize.remove();
    }

    card.classList.add('configurator__card--selected');
    card.setAttribute('aria-pressed', 'true');
    selectedBracelet = product;

    // Get size variants (option "Longueur" or option2)
    const sizeVariants = product.variants.filter(v => v.available);
    const hasSizes = sizeVariants.length > 1 || (sizeVariants.length === 1 && sizeVariants[0].option2);

    if (hasSizes) {
      // Default to no selection - user must choose
      selectedBraceletVariantId = null;

      // Create size selector inside the card
      const sizeSelector = document.createElement('div');
      sizeSelector.className = 'configurator__size-selector';

      const sizeBtns = document.createElement('div');
      sizeBtns.className = 'configurator__size-buttons';

      sizeVariants.forEach(variant => {
        const sizeValue = variant.option2 || variant.title;
        const btn = document.createElement('button');
        btn.className = 'configurator__size-btn';
        btn.setAttribute('type', 'button');
        btn.textContent = sizeValue;
        btn.dataset.variantId = variant.id;
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          // Deselect previous
          sizeBtns.querySelectorAll('.configurator__size-btn--active').forEach(b => b.classList.remove('configurator__size-btn--active'));
          btn.classList.add('configurator__size-btn--active');
          selectedBraceletVariantId = variant.id;
          // Update the bar - enable button
          braceletSelectedBar.style.display = 'flex';
          btnToStep2.disabled = false;
          btnToStep2.classList.remove('configurator__btn--disabled');
          braceletSelectedName.textContent = getCustomName(product) + ' - ' + sizeValue + ' ' + i18n.selected;
          announce(getCustomName(product) + ' ' + sizeValue + ' ' + i18n.selected);
        });
        sizeBtns.appendChild(btn);
      });

      sizeSelector.appendChild(sizeBtns);

      // Link to size guide drawer
      const guideLink = document.createElement('button');
      guideLink.className = 'configurator__size-guide-link';
      guideLink.setAttribute('type', 'button');
      guideLink.setAttribute('aria-controls', 'lbdb-size-guide-button');
      guideLink.setAttribute('aria-expanded', 'false');
      guideLink.textContent = 'Guide des tailles';
      sizeSelector.appendChild(guideLink);

      // Append size selector inside the card (after info)
      card.appendChild(sizeSelector);

      // Show bar immediately with disabled state (inciting to choose size)
      braceletSelectedBar.style.display = 'flex';
      braceletSelectedName.textContent = i18n.chooseSize;
      btnToStep2.disabled = true;
      btnToStep2.classList.add('configurator__btn--disabled');
    } else {
      // Single variant (no size choice needed)
      selectedBraceletVariantId = product.variants[0].id;
      braceletSelectedBar.style.display = 'flex';
      braceletSelectedName.textContent = getCustomName(product) + ' ' + i18n.selected;
      announce(getCustomName(product) + ' ' + i18n.selected);
    }
  }

  /* --- Charm Tag-based Categories --- */
  const CHARM_TAG_PREFIX = 'Collection Charm ';

  function getCharmTags(product) {
    const rawTags = product.tags;
    let tags = [];
    if (Array.isArray(rawTags)) {
      tags = rawTags.map(t => t.trim());
    } else if (typeof rawTags === 'string' && rawTags) {
      tags = rawTags.split(',').map(t => t.trim());
    }

    const categories = [];
    tags.forEach(tag => {
      if (tag.startsWith(CHARM_TAG_PREFIX) && tag !== 'Collection Charm') {
        categories.push(tag.substring(CHARM_TAG_PREFIX.length));
      }
    });
    return categories;
  }

  function charmMatchesFilter(product, filter) {
    if (filter === 'all') return true;
    return getCharmTags(product).includes(filter);
  }

  function charmMatchesSearch(product, query) {
    if (!query) return true;
    const q = query.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const name = getCustomName(product).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const title = product.title.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return name.includes(q) || title.includes(q);
  }

  /* --- Render Charms (Performance: show/hide instead of full re-render) --- */
  let charmsRendered = false;

  function renderCharmsInitial() {
    charmGrid.innerHTML = '';

    const validCharms = charms.filter(validateProduct);

    // Sort charms alphabetically by title
    validCharms.sort((a, b) => (a.title || '').localeCompare(b.title || '', document.documentElement.lang || 'fr'));

    if (validCharms.length === 0) {
      const msg = document.createElement('div');
      msg.className = 'configurator__no-results';
      msg.innerHTML = `<p>${i18n.noCharms}</p>`;
      charmGrid.appendChild(msg);
      return;
    }

    validCharms.forEach(product => {
      const card = createProductCard(product, 'charm');

      if (selectedCharms.find(c => c.id === product.id)) {
        card.classList.add('configurator__card--selected');
        card.setAttribute('aria-pressed', 'true');
      }

      const handleToggle = () => toggleCharm(product, card);
      card.addEventListener('click', handleToggle);
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        }
      });

      charmGrid.appendChild(card);
    });

    charmsRendered = true;
  }

  function renderCharms() {
    if (!charmsRendered) {
      renderCharmsInitial();
      applyCharmFilters();
      return;
    }
    applyCharmFilters();
  }

  function applyCharmFilters() {
    const cards = charmGrid.querySelectorAll('.configurator__card');
    let visibleCount = 0;

    cards.forEach(card => {
      const productId = card.dataset.productId;
      const product = charms.find(p => String(p.id) === String(productId));
      if (!product) {
        card.style.display = 'none';
        return;
      }

      const matchesSearch = charmMatchesSearch(product, searchQuery);
      // When search is active, ignore category filter (search across all categories)
      const matchesFilter = searchQuery ? true : charmMatchesFilter(product, activeFilter);
      const visible = matchesFilter && matchesSearch;

      card.style.display = visible ? '' : 'none';
      if (visible) visibleCount++;
    });

    // Show/hide no-results message
    let noResults = charmGrid.querySelector('.configurator__no-results');
    if (visibleCount === 0) {
      if (!noResults) {
        noResults = document.createElement('div');
        noResults.className = 'configurator__no-results';
        noResults.innerHTML = `<p>${i18n.noResults}</p>`;
        charmGrid.appendChild(noResults);
      }
      noResults.style.display = '';
    } else if (noResults) {
      noResults.style.display = 'none';
    }
  }

  function toggleCharm(product, card) {
    const index = selectedCharms.findIndex(c => c.id === product.id);

    if (index > -1) {
      selectedCharms.splice(index, 1);
      card.classList.remove('configurator__card--selected');
      card.setAttribute('aria-pressed', 'false');
      announce(getCustomName(product) + ' retiré');
    } else {
      if (selectedCharms.length >= MAX_CHARMS) {
        showToast(`Maximum ${MAX_CHARMS} charms atteint !`);
        announce(`Maximum ${MAX_CHARMS} charms atteint`);
        return;
      }
      selectedCharms.push(product);
      card.classList.add('configurator__card--selected');
      card.setAttribute('aria-pressed', 'true');
      announce(getCustomName(product) + ' ajouté');
    }

    updateCharmUI();
  }

  function updateCharmUI() {
    if (selectedCharms.length > 0) {
      selectedBar.style.display = 'flex';
      selectedList.innerHTML = '';
      selectedCharms.forEach(charm => {
        const img = document.createElement('img');
        img.className = 'configurator__selected-thumb';
        const imgSrc = (charm.images && charm.images[0]) ? charm.images[0].src : '';
        img.src = resizeImage(imgSrc, '100x100');
        img.alt = charm.title;
        selectedList.appendChild(img);
      });
    } else {
      selectedBar.style.display = 'none';
    }

    if (currentStep === 2) {
      btnNext.style.display = selectedCharms.length > 0 ? 'block' : 'none';
    }
  }

  /* --- Filters --- */
  function buildFilters() {
    const categories = new Set();
    if (charms && charms.length > 0) {
      charms.forEach(product => {
        getCharmTags(product).forEach(cat => categories.add(cat));
      });
    }

    charmCategories = Array.from(categories).sort();

    charmFilters.innerHTML = '';
    const allBtn = document.createElement('button');
    allBtn.className = 'configurator__filter configurator__filter--active';
    allBtn.dataset.filter = 'all';
    allBtn.textContent = i18n.filterAll;
    allBtn.setAttribute('aria-pressed', 'true');
    charmFilters.appendChild(allBtn);

    charmCategories.forEach(cat => {
      const btn = document.createElement('button');
      btn.className = 'configurator__filter';
      btn.dataset.filter = cat;
      btn.textContent = CATEGORY_TRANSLATIONS[cat] || cat;
      btn.setAttribute('aria-pressed', 'false');
      charmFilters.appendChild(btn);
    });

    charmFilters.addEventListener('click', (e) => {
      if (e.target.classList.contains('configurator__filter')) {
        charmFilters.querySelectorAll('.configurator__filter').forEach(f => {
          f.classList.remove('configurator__filter--active');
          f.setAttribute('aria-pressed', 'false');
        });
        e.target.classList.add('configurator__filter--active');
        e.target.setAttribute('aria-pressed', 'true');
        activeFilter = e.target.dataset.filter;
        renderCharms();
      }
    });
  }

  /* --- Search --- */
  let searchTimeout = null;

  charmSearch.addEventListener('input', (e) => {
    const query = e.target.value.trim();
    charmSearchClear.style.display = query ? 'flex' : 'none';

    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      searchQuery = query;
      renderCharms();
    }, 200);
  });

  charmSearchClear.addEventListener('click', () => {
    charmSearch.value = '';
    charmSearchClear.style.display = 'none';
    searchQuery = '';
    renderCharms();
    charmSearch.focus();
  });

  /* --- Summary with Promo --- */
  function renderSummary() {
    const summaryBracelet = document.getElementById('summary-bracelet');
    const summaryCharms = document.getElementById('summary-charms');
    const summaryCharmCount = document.getElementById('summary-charm-count');
    const summaryTotal = document.getElementById('summary-total');
    const summaryPromo = document.getElementById('summary-promo');
    const summaryTotalBefore = document.getElementById('summary-total-before');

    const promo = calculatePromo();

    // Find bracelet in promo items
    const braceletPromo = promo.items.find(i => i.role === 'base');

    if (braceletPromo) {
      summaryBracelet.innerHTML = '';
      const img = document.createElement('img');
      img.className = 'configurator__summary-item-image';
      const imgSrc = (braceletPromo.product.images && braceletPromo.product.images[0]) ? braceletPromo.product.images[0].src : '';
      img.src = resizeImage(imgSrc, '200x200');
      img.alt = braceletPromo.product.title;

      const infoDiv = document.createElement('div');
      infoDiv.className = 'configurator__summary-item-info';

      const nameDiv = document.createElement('div');
      nameDiv.className = 'configurator__summary-item-name';
      nameDiv.textContent = getCustomName(braceletPromo.product);

      // Show selected size
      if (selectedBraceletVariantId) {
        const selectedVariant = braceletPromo.product.variants.find(v => v.id === selectedBraceletVariantId);
        if (selectedVariant && selectedVariant.option2) {
          const sizeDiv = document.createElement('div');
          sizeDiv.className = 'configurator__summary-item-size';
          sizeDiv.textContent = 'Taille : ' + selectedVariant.option2 + ' ';
          const guideLink = document.createElement('button');
          guideLink.className = 'configurator__size-guide-link';
          guideLink.setAttribute('type', 'button');
          guideLink.setAttribute('aria-controls', 'lbdb-size-guide-button');
          guideLink.setAttribute('aria-expanded', 'false');
          guideLink.textContent = 'Guide des tailles';
          sizeDiv.appendChild(guideLink);
          nameDiv.appendChild(sizeDiv);
        }
      }

      const priceDiv = document.createElement('div');
      priceDiv.className = 'configurator__summary-item-price';

      const hasDiscount = braceletPromo.discountLabel !== null;
      if (hasDiscount) {
        const origSpan = document.createElement('span');
        origSpan.className = 'configurator__price-original';
        origSpan.textContent = formatPrice(braceletPromo.originalPrice.toString());
        priceDiv.appendChild(origSpan);
      }

      const priceSpan = document.createElement('span');
      if (hasDiscount) priceSpan.className = 'configurator__price-discounted';
      priceSpan.textContent = formatPrice(braceletPromo.discountedPrice.toString());
      priceDiv.appendChild(priceSpan);

      if (hasDiscount) {
        const badge = document.createElement('span');
        badge.className = 'configurator__price-badge';
        badge.textContent = braceletPromo.discountLabel;
        priceDiv.appendChild(badge);
      }

      infoDiv.appendChild(nameDiv);
      infoDiv.appendChild(priceDiv);
      summaryBracelet.appendChild(img);
      summaryBracelet.appendChild(infoDiv);
    }

    // Charms
    const charmPromos = promo.items.filter(i => i.role === 'charm');
    summaryCharmCount.textContent = charmPromos.length;

    summaryCharms.innerHTML = '';
    charmPromos.forEach(item => {
      const charmDiv = document.createElement('div');
      charmDiv.className = 'configurator__summary-charm';

      const img = document.createElement('img');
      img.className = 'configurator__summary-charm-image';
      const imgSrc = (item.product.images && item.product.images[0]) ? item.product.images[0].src : '';
      img.src = resizeImage(imgSrc, '100x100');
      img.alt = item.product.title;

      const nameSpan = document.createElement('span');
      nameSpan.className = 'configurator__summary-charm-name';
      nameSpan.textContent = getCustomName(item.product);

      const priceSpan = document.createElement('span');
      priceSpan.className = 'configurator__summary-charm-price';

      const hasDiscount = item.discountLabel !== null;
      if (hasDiscount) {
        const origSpan = document.createElement('span');
        origSpan.className = 'configurator__price-original';
        origSpan.textContent = formatPrice(item.originalPrice.toString());
        priceSpan.appendChild(origSpan);
      }

      const valSpan = document.createElement('span');
      if (hasDiscount) valSpan.className = 'configurator__price-discounted';
      valSpan.textContent = formatPrice(item.discountedPrice.toString());
      priceSpan.appendChild(valSpan);

      if (hasDiscount) {
        const badge = document.createElement('span');
        badge.className = 'configurator__price-badge';
        badge.textContent = item.discountLabel;
        priceSpan.appendChild(badge);
      }

      // Remove button - use product ID instead of array index to avoid index shift bug
      const removeBtn = document.createElement('button');
      removeBtn.className = 'configurator__summary-charm-remove';
      removeBtn.dataset.productId = item.product.id;
      removeBtn.setAttribute('aria-label', `Retirer ${getCustomName(item.product)}`);
      removeBtn.textContent = '\u2715';

      removeBtn.addEventListener('click', () => {
        const idx = selectedCharms.findIndex(c => c.id === item.product.id);
        if (idx >= 0) {
          selectedCharms.splice(idx, 1);
          // Update the charm grid card state
          const gridCard = charmGrid.querySelector(`[data-product-id="${item.product.id}"]`);
          if (gridCard) {
            gridCard.classList.remove('configurator__card--selected');
            gridCard.setAttribute('aria-pressed', 'false');
          }
          updateCharmUI();
          renderSummary();
          announce(getCustomName(item.product) + ' retiré du récapitulatif');
        }
      });

      charmDiv.appendChild(img);
      charmDiv.appendChild(nameSpan);
      charmDiv.appendChild(priceSpan);
      charmDiv.appendChild(removeBtn);
      summaryCharms.appendChild(charmDiv);
    });

    // Promo banner
    if (promo.savings > 0 && summaryPromo) {
      summaryPromo.style.display = 'block';
      summaryPromo.innerHTML = '';
      const icon = document.createElement('span');
      icon.className = 'configurator__promo-icon';
      icon.textContent = '🎉';
      const text = document.createElement('span');
      text.innerHTML = i18n.savings.replace('{{ amount }}', escapeHtml(formatPrice(promo.savings.toString())));
      summaryPromo.appendChild(icon);
      summaryPromo.appendChild(text);
    } else if (summaryPromo) {
      summaryPromo.style.display = 'none';
    }

    // Total
    if (promo.savings > 0 && summaryTotalBefore) {
      summaryTotalBefore.style.display = 'block';
      summaryTotalBefore.textContent = formatPrice(promo.totalBefore.toString());
    } else if (summaryTotalBefore) {
      summaryTotalBefore.style.display = 'none';
    }

    summaryTotal.textContent = formatPrice(promo.totalAfter.toString());
  }

  /* --- Add to Cart (with race condition guard) --- */
  btnAddToCart.addEventListener('click', async () => {
    if (!selectedBracelet || !selectedBraceletVariantId || isAddingToCart) return;

    isAddingToCart = true;
    btnAddToCart.disabled = true;
    btnAddToCart.textContent = i18n.adding;

    // Snapshot the current selection to avoid race conditions
    const braceletToAdd = selectedBracelet;
    const charmsToAdd = [...selectedCharms];

    const items = [];

    items.push({
      id: selectedBraceletVariantId,
      quantity: 1,
      properties: {
        '_bundle': 'compose-ton-bracelet',
        '_bundle_role': 'base',
        'Composition': `Base + ${charmsToAdd.length} charm(s)`
      }
    });

    charmsToAdd.forEach((charm, i) => {
      items.push({
        id: charm.variants[0].id,
        quantity: 1,
        properties: {
          '_bundle': 'compose-ton-bracelet',
          '_bundle_role': 'charm',
          '_bundle_position': (i + 1).toString(),
          'Pour bracelet': getCustomName(braceletToAdd)
        }
      });
    });

    try {
      // Get the cart-drawer section ID for bundled section rendering
      const cartDrawerEl = document.querySelector('cart-drawer');
      const cartDrawerSectionId = cartDrawerEl ? (cartDrawerEl.closest('.shopify-section')?.id?.replace('shopify-section-', '') || 'cart-drawer') : 'cart-drawer';

      const response = await fetch(`${rootUrl}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items,
          sections: [cartDrawerSectionId, 'cart-icon-bubble']
        })
      });

      if (response.ok) {
        const responseData = await response.json();
        announce(i18n.successTitle);
        // Fetch fresh cart with sections for drawer update
        const cartRes = await fetch(`${rootUrl}cart.js`);
        const cartData = await cartRes.json();
        // Merge sections from add response into cart data
        cartData.sections = responseData.sections || {};
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: cartData } }));
        // Show success screen
        showSuccessScreen();
      } else {
        let errorMsg = "Impossible d'ajouter au panier";
        try {
          const error = await response.json();
          errorMsg = error.description || errorMsg;
        } catch (e) { /* ignore parse error */ }
        showToast('Erreur : ' + errorMsg);
        btnAddToCart.disabled = false;
        btnAddToCart.textContent = i18n.addToCart;
        isAddingToCart = false;
      }
    } catch (err) {
      showToast('Erreur réseau, veuillez réessayer');
      btnAddToCart.disabled = false;
      btnAddToCart.textContent = i18n.addToCart;
      isAddingToCart = false;
    }
  });

  /* --- Success Screen --- */
  function showSuccessScreen() {
    // Hide all panels and stepper
    panels.forEach(p => p.style.display = 'none');
    configurator.querySelector('.configurator__stepper').style.display = 'none';
    // Hide bottom bars
    braceletSelectedBar.style.display = 'none';
    const navBar = configurator.querySelector('.configurator__nav');
    if (navBar) navBar.style.display = 'none';

    // Create success screen
    const successScreen = document.createElement('div');
    successScreen.className = 'configurator__success';

    const icon = document.createElement('div');
    icon.className = 'configurator__success-icon';
    icon.textContent = '\u2713';

    const title = document.createElement('h2');
    title.className = 'configurator__success-title';
    title.textContent = i18n.successTitle;

    const msg = document.createElement('p');
    msg.className = 'configurator__success-msg';
    msg.textContent = i18n.successMsg;

    const btnNew = document.createElement('button');
    btnNew.className = 'configurator__success-btn';
    btnNew.setAttribute('type', 'button');
    btnNew.textContent = i18n.successNew;
    btnNew.addEventListener('click', () => {
      window.location.reload();
    });

    const linkHome = document.createElement('a');
    linkHome.className = 'configurator__success-home';
    linkHome.href = rootUrl;
    linkHome.textContent = i18n.successHome;

    successScreen.appendChild(icon);
    successScreen.appendChild(title);
    successScreen.appendChild(msg);
    successScreen.appendChild(btnNew);
    successScreen.appendChild(linkHome);

    configurator.appendChild(successScreen);
    configurator.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* --- Utilities --- */

  // Metafield maps: product numeric ID -> nom_court / type_charm (fetched via Storefront API GraphQL)
  let namesMap = {};
  let typeCharmMap = {};

  /**
   * Fetch custom.nom_court and custom.type_charm metafields for all products
   * in a collection via the Storefront API GraphQL endpoint.
   */
  async function fetchMetafieldsMap(collectionHandle) {
    const shopDomain = configurator.dataset.shopDomain || window.Shopify?.shop || '';
    if (!shopDomain) return;
    const endpoint = `https://${shopDomain}/api/2024-01/graphql.json`;
    // Retrieve Storefront Access Token from page meta tags
    const metaTag = document.querySelector('meta[name="shopify-checkout-api-token"]');
    const storefrontToken = (metaTag && metaTag.content) || '';
    if (!storefrontToken) return;

    let hasNextPage = true;
    let cursor = null;

    while (hasNextPage) {
      const afterClause = cursor ? `, after: "${cursor}"` : '';
      const query = `{
        collection(handle: "${collectionHandle}") {
          products(first: 250${afterClause}) {
            pageInfo { hasNextPage endCursor }
            edges {
              node {
                id
                nomCourt: metafield(namespace: "custom", key: "nom_court") { value }
                typeCharm: metafield(namespace: "custom", key: "type_charm") { value }
              }
            }
          }
        }
      }`;

      try {
        const resp = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': storefrontToken,
            'Accept-Language': LOCALE
          },
          body: JSON.stringify({ query })
        });
        if (!resp.ok) break;
        const json = await resp.json();
        const products = json?.data?.collection?.products;
        if (!products) break;

        products.edges.forEach(({ node }) => {
          const numericId = node.id.replace('gid://shopify/Product/', '');
          if (node.nomCourt && node.nomCourt.value) {
            namesMap[numericId] = node.nomCourt.value;
          }
          if (node.typeCharm && node.typeCharm.value) {
            typeCharmMap[numericId] = node.typeCharm.value;
          }
        });

        hasNextPage = products.pageInfo.hasNextPage;
        cursor = products.pageInfo.endCursor;
      } catch (e) {
        console.warn('fetchMetafieldsMap error:', e);
        break;
      }
    }
  }

  function getCustomName(product) {
    if (!product || !product.title) return '';
    // Priority 1: nom_court from Storefront API metafield map
    const shortName = namesMap[String(product.id)];
    if (shortName) return shortName;
    // Fallback: extract "modèle X" from title
    const modelMatch = product.title.match(/mod[eè]le\s+(\w+)/i);
    if (modelMatch) return modelMatch[1];
    return product.title;
  }

  function getTypeCharm(product) {
    if (!product) return '';
    return typeCharmMap[String(product.id)] || '';
  }

  function formatPrice(price) {
    const num = parseFloat(price);
    if (isNaN(num)) return '0,00 €';
    // Convert price to cents (Shopify formatMoney expects cents)
    const cents = Math.round(num * 100);
    // Apply currency rate for Shopify Markets (converts from shop currency to visitor's market currency)
    const rate = parseFloat(window.Shopify?.currency?.rate) || 1;
    const convertedCents = Math.round(cents * rate);
    // Use theme's formatMoney if available, otherwise fallback to Intl
    if (typeof window.formatMoney === 'function') {
      const format = window.themeVariables?.settings?.currencyCodeEnabled
        ? window.themeVariables.settings.moneyWithCurrencyFormat
        : window.themeVariables.settings.moneyFormat;
      return formatMoney(convertedCents, format);
    }
    // Fallback: use Intl.NumberFormat with detected currency
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || navigator.language || 'fr-FR';
    return (convertedCents / 100).toLocaleString(locale, { style: 'currency', currency: currency });
  }

  function resizeImage(src, size) {
    if (!src) return '';
    // Use Shopify CDN width parameter for better compatibility with /files/ URLs
    if (src.includes('/files/')) {
      const width = size.split('x')[0];
      const separator = src.includes('?') ? '&' : '?';
      return src + separator + 'width=' + width;
    }
    return src.replace(/(\.[a-z]+)(\?.*)?$/i, `_${size}$1$2`);
  }

  function showToast(message) {
    let toast = document.querySelector('.configurator__toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'configurator__toast';
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('configurator__toast--visible');
    setTimeout(() => toast.classList.remove('configurator__toast--visible'), 2500);
  }

  /* --- Cleanup (Memory leak prevention) --- */
  function cleanup() {
    if (stickyObserver) stickyObserver.disconnect();
    clearTimeout(searchTimeout);
  }

  window.addEventListener('beforeunload', cleanup);

  /* --- Init --- */
  async function init() {
    try {
      // Fetch nom_court metafields in parallel with product data
      const namesPromise = Promise.all([
        fetchMetafieldsMap(BRACELET_COLLECTION),
        fetchMetafieldsMap(CHARM_COLLECTION)
      ]);

      bracelets = await fetchCollectionProducts(BRACELET_COLLECTION);
      charms = await fetchCollectionProducts(CHARM_COLLECTION);

      // Wait for nom_court data before rendering
      await namesPromise;

      renderBracelets();
      buildFilters();
      renderCharmsInitial();
    } catch (err) {
      console.error('Configurator init error:', err);
      const errorMsg = document.createElement('p');
      errorMsg.style.textAlign = 'center';
      errorMsg.style.color = '#c00';
      errorMsg.textContent = 'Erreur lors du chargement des produits. Veuillez rafraîchir la page.';
      braceletGrid.appendChild(errorMsg);
    }
  }

  init();

})();
// v2.1 - type_charm + nom_court
